import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import BuilderLogin from "./BuilderLogin";
import PopupTargetController from "./PopupTargetController";
import Device from "../utils/Device";
import D3EAppTemplateCategories from "../classes/D3EAppTemplateCategories";
import D3EAppTemplate from "../models/D3EAppTemplate";
import Popup from "./Popup";
import MaterialIcons from "../icons/MaterialIcons";
import PageNavigator from "../classes/PageNavigator";
import FailureMessageView from "./FailureMessageView";
import D3EAppTemplateSelectionView from "./D3EAppTemplateSelectionView";
import Query from "../classes/Query";
import MessageDispatch from "../rocket/MessageDispatch";
import BuildTemplateListView from "./BuildTemplateListView";
import D3EAppTemplateCategoriesRequest from "../models/D3EAppTemplateCategoriesRequest";
import Button from "./Button";
import TextWithIcon from "./TextWithIcon";
import ListWrapper from "../utils/ListWrapper";
import Developer from "../models/Developer";
import FeatureUtils from "../classes/FeatureUtils";
import TemplateListView from "./TemplateListView";
import TextView from "./TextView";
import D3EAppTemplateCategory from "../models/D3EAppTemplateCategory";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";
import Browser from "../classes/Browser";
import ContainerEx from "./ContainerEx";

type _SignInButtonOnPressed = (d3eState: StudioAppsPageRefs) => void;

type _BuildButtonOnPressed = (d3eState: StudioAppsPageRefs) => void;

export interface StudioAppsPageProps extends BaseUIProps {
  key?: string;
}
/// To store state data for StudioAppsPage
class StudioAppsPageRefs {
  public buildButton: BuildButtonState = new BuildButtonState();
  listViewController: ui.ScrollController = new ui.ScrollController();
  public signInButton: SignInButtonState = new SignInButtonState();
}

interface BuildButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StudioAppsPageRefs;
  _onBuildButtonHandler?: _BuildButtonOnPressed;
}

class BuildButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public _focus: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get focus(): boolean {
    return this._focus;
  }
  public setFocus(val: boolean) {
    let isValChanged: boolean = this._focus !== val;

    if (!isValChanged) {
      return;
    }

    this._focus = val;

    this.fire("focus", this);
  }
}

class _BuildButtonWithState extends ObservableComponent<BuildButtonWithStateProps> {
  buildButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: BuildButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get buildButton(): BuildButtonState {
    return this.props.d3eState.buildButton;
  }
  public get d3eState(): StudioAppsPageRefs {
    return this.props.d3eState;
  }
  public get _onBuildButtonHandler(): _BuildButtonOnPressed {
    return this.props._onBuildButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("buildButton", null, this.buildButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["buildButton", "buildButton.", "buildButton.focus", "buildButton.hover"],
      this.rebuild
    );
  }
  public buildButtonOnEnter(event): void {
    return this.buildButton.setHover(true);
  }
  public buildButtonOnExit(event): void {
    return this.buildButton.setHover(false);
  }
  public buildButtonOnFocusChange(val): void {
    return this.buildButton.setFocus(val);
  }
  public dispose(): void {
    this.buildButton.setHover(false);

    this.buildButton.setFocus(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 10.0,
        vertical: 0.0,
        transitions: new Map(),
      }),
      width: 90,
      child: Button({
        padding: cStyle.tButtonPrimaryButtonPaddingOn,
        decoration: this.buildButton.hover
          ? cStyle.tButtonPrimaryButtonDecorationOnHover
          : this.buildButton.focus
            ? cStyle.tButtonPrimaryButtonDecorationOnFocus
            : cStyle.tButtonPrimaryButtonDecorationOn,
        disable: this.buildButton.disable,
        onPressed: () => {
          this._onBuildButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {
          this.buildButtonOnFocusChange(val);
        },
        child: TextView({
          data: "Build Now",
          style: new ui.TextStyle({ fontSize: 13 }),
          className: "x065 vc",
        }),
        onEnter: (event) => {
          this.buildButtonOnEnter(event);
        },
        onExit: (event) => {
          this.buildButtonOnExit(event);
        },
      }),
      className: "xc5a1 hc vc",
    });
  }
}
function BuildButtonWithState(props: BuildButtonWithStateProps) {
  return React.createElement(_BuildButtonWithState, props);
}

interface SignInButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: StudioAppsPageRefs;
  _onSignInButtonHandler?: _SignInButtonOnPressed;
}

class SignInButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _SignInButtonWithState extends ObservableComponent<SignInButtonWithStateProps> {
  signInButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SignInButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get signInButton(): SignInButtonState {
    return this.props.d3eState.signInButton;
  }
  public get d3eState(): StudioAppsPageRefs {
    return this.props.d3eState;
  }
  public get _onSignInButtonHandler(): _SignInButtonOnPressed {
    return this.props._onSignInButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("signInButton", null, this.signInButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["signInButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.all(10.0, new Map()),
      child: Button({
        padding: cStyle.tButtonOutlineButtonPaddingOn,
        decoration: cStyle.tButtonOutlineButtonDecorationOn,
        disable: this.signInButton.disable,
        onPressed: () => {
          this._onSignInButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => { },
        child: TextView({ data: "Sign In", className: "vc" }),
      }),
      className: "x2aa vc",
    });
  }
}
function SignInButtonWithState(props: SignInButtonWithStateProps) {
  return React.createElement(_SignInButtonWithState, props);
}

class _StudioAppsPageState extends ObservableComponent<StudioAppsPageProps> {
  d3eState: StudioAppsPageRefs = new StudioAppsPageRefs();
  templateCatergories: Array<D3EAppTemplateCategory> = ListWrapper.widget(
    this,
    "templateCatergories"
  );
  appTemplates: Array<D3EAppTemplate> = ListWrapper.widget(
    this,
    "appTemplates"
  );
  selectedCategories: Array<D3EAppTemplateCategory> = ListWrapper.widget(
    this,
    "selectedCategories"
  );
  categoryQuery: D3EAppTemplateCategories = null;
  request: D3EAppTemplateCategoriesRequest = null;
  user: Developer = null;
  message: string = "";
  loginPopupPopup: Popup;
  failureMessagePopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public rightSideViewPopupTargetController: PopupTargetController =
    new PopupTargetController();
  public constructor(props: StudioAppsPageProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      ["categoryQuery", "categoryQuery.items"],
      this.computeTemplateCatergories
    );

    this.computeTemplateCatergories();

    this.on(
      ["request", "request.offset", "request.orderBy", "request.pageSize"],
      this.computeCategoryQuery
    );

    this.computeCategoryQuery();

    this.computeUser();

    this.on(
      ["appTemplates", "selectedCategories", "templateCatergories", "user"],
      this.rebuild
    );
  }
  public setTemplateCatergories(val: Array<D3EAppTemplateCategory>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.templateCatergories,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl(
      "templateCatergories",
      this.templateCatergories,
      val
    );

    this.templateCatergories.clear();

    this.templateCatergories.addAll(val);

    this.fire("templateCatergories", this);
  }
  public addToTemplateCatergories(
    val: D3EAppTemplateCategory,
    index: number = -1
  ): void {
    if (index === -1) {
      if (!this.templateCatergories.contains(val))
        this.templateCatergories.add(val);
    } else {
      this.templateCatergories.remove(
        this.templateCatergories.elementAt(index)
      );

      this.templateCatergories.add(val);
    }

    this.fire("templateCatergories", this, val, true);

    this.updateObservable("templateCatergories", null, val);
  }
  public removeFromTemplateCatergories(val: D3EAppTemplateCategory): void {
    this.templateCatergories.remove(val);

    this.fire("templateCatergories", this, val, false);

    this.removeObservable("templateCatergories", val);
  }
  public computeTemplateCatergories = (): void => {
    try {
      this.setTemplateCatergories(
        Array.from(this.categoryQuery !== null ? this.categoryQuery.items : [])
      );
    } catch (exception) {
      console.log(
        " exception in computeTemplateCatergories : " + exception.toString()
      );

      this.setTemplateCatergories([]);
    }
  };
  public setAppTemplates(val: Array<D3EAppTemplate>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.appTemplates,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("appTemplates", this.appTemplates, val);

    this.appTemplates.clear();

    this.appTemplates.addAll(val);

    this.fire("appTemplates", this);
  }
  public addToAppTemplates(val: D3EAppTemplate, index: number = -1): void {
    if (index === -1) {
      if (!this.appTemplates.contains(val)) this.appTemplates.add(val);
    } else {
      this.appTemplates.remove(this.appTemplates.elementAt(index));

      this.appTemplates.add(val);
    }

    this.fire("appTemplates", this, val, true);

    this.updateObservable("appTemplates", null, val);
  }
  public removeFromAppTemplates(val: D3EAppTemplate): void {
    this.appTemplates.remove(val);

    this.fire("appTemplates", this, val, false);

    this.removeObservable("appTemplates", val);
  }
  public setSelectedCategories(val: Array<D3EAppTemplateCategory>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.selectedCategories,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl(
      "selectedCategories",
      this.selectedCategories,
      val
    );

    this.selectedCategories.clear();

    this.selectedCategories.addAll(val);

    this.fire("selectedCategories", this);
  }
  public addToSelectedCategories(
    val: D3EAppTemplateCategory,
    index: number = -1
  ): void {
    if (index === -1) {
      if (!this.selectedCategories.contains(val))
        this.selectedCategories.add(val);
    } else {
      this.selectedCategories.remove(this.selectedCategories.elementAt(index));

      this.selectedCategories.add(val);
    }

    this.fire("selectedCategories", this, val, true);

    this.updateObservable("selectedCategories", null, val);
  }
  public removeFromSelectedCategories(val: D3EAppTemplateCategory): void {
    this.selectedCategories.remove(val);

    this.fire("selectedCategories", this, val, false);

    this.removeObservable("selectedCategories", val);
  }
  public setCategoryQuery(val: D3EAppTemplateCategories): void {
    let isValChanged: boolean = this.categoryQuery !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("categoryQuery", this.categoryQuery, val);

    MessageDispatch.get().dispose(this.categoryQuery);

    this.categoryQuery = val;

    this.fire("categoryQuery", this);
  }
  public computeCategoryQuery = async (): Promise<void> => {
    try {
      this.setCategoryQuery(
        await Query.get().getD3EAppTemplateCategories(
          UsageConstants.QUERY_GETD3EAPPTEMPLATECATEGORIES_STUDIOAPPSPAGE_PROPERTIES_CATEGORYQUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeCategoryQuery : " + exception.toString()
      );

      this.setCategoryQuery(null);
    }
  };
  public setRequest(val: D3EAppTemplateCategoriesRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setUser(val: Developer): void {
    let isValChanged: boolean = this.user !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("user", this.user, val);

    this.user = val;

    this.fire("user", this);
  }
  public computeUser = async (): Promise<void> => {
    try {
      this.setUser(
        await Query.get().getDeveloperById(
          UsageConstants.QUERY_LOADDEVELOPER_STUDIOAPPSPAGE_PROPERTIES_USER_COMPUTATION,
          ((await Query.get().currentUser()) as Developer).id
        )
      );
    } catch (exception) {
      console.log(" exception in computeUser : " + exception.toString());

      this.setUser(null);
    }
  };
  public setMessage(val: string): void {
    let isValChanged: boolean = this.message !== val;

    if (!isValChanged) {
      return;
    }

    this.message = val;

    this.fire("message", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      children: [
        ui.Column({
          children: [
            ui.Row({
              children: [
                ui.GestureDetector({
                  onTap: () => {
                    Browser.open("https://new.d3e.studio");
                  },
                  child: ContainerEx.from({
                    margin: ui.EdgeInsets.only({left: 10}),
                    child: ui.AssetImage({
                      path: "images/headerlogo.png",
                      width: 90,
                      height: 50,
                      fit: ui.BoxFit.contain,
                      className: "x88 hc vc",
                      key: "0",
                    }),
                  })
                })
              ],
              className: "x85 hc vc h",
              key: "0",
            }),
            ui.ListView({
              controller: this.d3eState.listViewController,
              children: [
                this.templateCatergories.expand((item) => [
                  D3EAppTemplateSelectionView({
                    template: item,
                    onAddTemplateCategory: (category) => {
                      this.onTemplateSelectedHandler(
                        category,
                        item,
                        this.d3eState
                      );
                    },
                    onRemoveTemplateCategory: (category) => {
                      this.onRemoveTemplateSelectedHandler(
                        category,
                        item,
                        this.d3eState
                      );
                    },
                    className: "hc",
                    key: item?.ident,
                  }),
                ]),
              ],
              className: "xad hc vc v",
              key: "1",
            }),
          ],
          className: "x46 hc vc v",
          key: "0",
        }),
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              children: [
                TextView({
                  data: "Choose a base",
                  style: new ui.TextStyle({ color: cStyle.c4, fontSize: 14 }),
                  className: "xed4 vc",
                  key: "0",
                }),
                this.user === null
                  ? SignInButtonWithState({
                    d3eState: this.d3eState,
                    _onSignInButtonHandler: this.onSignInButtonHandler,
                  })
                  : TextWithIcon({
                    title: "Log out",
                    icon: MaterialIcons.logout,
                    size: 25,
                    tooltip: "Log out",
                    color: new ui.Color(0xff000000),
                    hoverColor: new ui.Color(0xff000000),
                    onPressed: () => {
                      this.onPressedLogoutButton2Handler(this.d3eState);
                    },
                    className: "vc",
                  }),
              ],
              className: "x583 hc vc h",
              key: "0",
            }),
            this.selectedCategories.isNotEmpty
              ? ui.Container({
                expand: true,
                child: TemplateListView({
                  categories: this.selectedCategories,
                  appTemplateList: this.appTemplates,
                  onAddBuildTemplate: (template) => {
                    this.onBuildTemplateSelectedHandler(
                      template,
                      this.d3eState
                    );
                  },
                  onRemoveBuildTemplate: (template) => {
                    this.onRemoveBuildTemplateSelectedHandler(
                      template,
                      this.d3eState
                    );
                  },
                  key: this.selectedCategories.length.toString(),
                }),
                className: "x72a hc vc v",
              })
              : ui.Container({
                expand: true,
                child: TemplateListView({
                  categories: this.templateCatergories,
                  appTemplateList: this.appTemplates,
                  onAddBuildTemplate: (template) => {
                    this.onAddTemplateListRefHandler(template, this.d3eState);
                  },
                  onRemoveBuildTemplate: (template) => {
                    this.onRemoveTemplateListRefHandler(
                      template,
                      this.d3eState
                    );
                  },
                  key: this.templateCatergories.length.toString() + "all",
                }),
                className: "x997 hc vc v",
              }),
            this.appTemplates.isNotEmpty
              ? ui.Row({
                mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
                crossAxisAlignment: ui.CrossAxisAlignment.center,
                children: [
                  ui.Container({
                    margin: ui.EdgeInsets.all(10.0, new Map()),
                    child: BuildTemplateListView({
                      templates: this.appTemplates,
                      onClearSelection: (items) => {
                        this.onClearselection(items, this.d3eState);
                      },
                    }),
                    key: "0",
                    className: "x2a7 vc",
                  }),
                  ui.Row({
                    mainAxisAlignment: ui.MainAxisAlignment.start,
                    crossAxisAlignment: ui.CrossAxisAlignment.center,
                    children: [
                      TextView({
                        data:
                          this.appTemplates.length.toString() + " Selected",
                        textAlign: ui.TextAlign.center,
                        style: new ui.TextStyle({
                          color: cStyle.c4,
                          fontSize: 16,
                        }),
                        className: "xcb6 vc",
                        key: "0",
                      }),
                      BuildButtonWithState({
                        d3eState: this.d3eState,
                        _onBuildButtonHandler: this.onBuildButtonHandler,
                        key: "1",
                      }),
                    ],
                    className: "vc",
                    key: "1",
                  }),
                ],
                className: "x9f6 hc vc h",
              })
              : [],
          ],
          d3eRef: this.rightSideViewPopupTargetController.handleRef,
          className: "x78e hc vc h v",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "StudioAppsPage hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new D3EAppTemplateCategoriesRequest({
        pageSize: 1000,
        offset: 0,
        orderBy: "",
      })
    );
  };
  public onClearselection = (
    items: Array<D3EAppTemplate>,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.setAppTemplates([]);
  };
  public onBuildButtonHandler = (d3eState: StudioAppsPageRefs): void => {
    this.setMessage("");

    if (this.appTemplates.isNotEmpty) {
      this.navigator.pushStudioFeaturesPage({
        featureList: FeatureUtils.getFeaturesByTemplate(this.appTemplates),
        templates: this.appTemplates,
      });
    } else {
      this.setMessage("Please select atleast one template");

      this.showFailureMessage({ autoClose: true });
    }
  };
  public onTemplateSelectedHandler = (
    category: D3EAppTemplateCategory,
    item: D3EAppTemplateCategory,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.selectedCategories.add(category);
  };
  public onRemoveTemplateSelectedHandler = (
    category: D3EAppTemplateCategory,
    item: D3EAppTemplateCategory,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.selectedCategories.remove(category);
  };
  public onBuildTemplateSelectedHandler = (
    template: D3EAppTemplate,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.appTemplates.add(template);
  };
  public onRemoveBuildTemplateSelectedHandler = (
    template: D3EAppTemplate,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.appTemplates.remove(template);
  };
  public onAddTemplateListRefHandler = (
    template: D3EAppTemplate,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.appTemplates.add(template);
  };
  public onRemoveTemplateListRefHandler = (
    template: D3EAppTemplate,
    d3eState: StudioAppsPageRefs
  ): void => {
    this.appTemplates.remove(template);
  };
  public onSignInButtonHandler = (d3eState: StudioAppsPageRefs): void => {
    this.showLoginPopup({ autoClose: true });
  };
  public moveToStudioFeaturesPageRef = (developer: Developer): void => {
    this.setUser(developer);

    this.hideLoginPopup();
  };
  public onPressedLogoutButton2Handler = async (
    d3eState: StudioAppsPageRefs
  ): Promise<void> => {
    Device.put("email", null);

    Device.put("password", null);

    let result: boolean = await Query.get().logout();

    this.setUser(null);

    this.hideLoginPopup();
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.categoryQuery);

    this.loginPopupPopup?.dispose();

    this.failureMessagePopup?.dispose();

    super.dispose();
  }
  public showLoginPopup(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.loginPopupPopup?.dispose();

    this.loginPopupPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 600,
        child: BuilderLogin({
          onLoginUser: (developer) => {
            this.moveToStudioFeaturesPageRef(developer);
          },
        }),
        className: "x11d hc vc",
      }),
    });

    this.loginPopupPopup.showPopup(this.context);
  }
  public showFailureMessage(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.failureMessagePopup?.dispose();

    let target: ui.Rect = this.rightSideViewPopupTargetController.getTarget(
      this.context
    );

    this.failureMessagePopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Top,
      child: ui.Container({
        height: 50,
        margin: ui.EdgeInsets.fromLTRB(0.0, 110.0, 15.0, 0.0, new Map()),
        child: FailureMessageView({ msg: this.message }),
        className: "xc55 hc vc",
      }),
      target: target,
    });

    this.failureMessagePopup.showPopup(this.context);
  }
  public hideLoginPopup(): void {
    this.loginPopupPopup?.dispose();
  }
  public hideFailureMessage(): void {
    this.failureMessagePopup?.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get buildButton() {
    return this.d3eState.buildButton;
  }
  public get signInButton() {
    return this.d3eState.signInButton;
  }
}
export default function StudioAppsPage(props: StudioAppsPageProps) {
  return React.createElement(_StudioAppsPageState, props);
}
